import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useBranch } from "@/contexts/BranchContext";
import { useBranchFreeze } from "@/lib/useBranchFreeze";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  CalendarClock, Plus, Lock, Unlock, Save, ChevronDown, ChevronUp,
  ArrowRightLeft, Trash2, AlertCircle, FolderOpen, Building2,
} from "lucide-react";
import { PAYMENT_MODES, formatMode } from "@/lib/paymentModes";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d + (d.length === 10 ? "T00:00:00" : "")).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return d; }
};

/**
 * Small orange pill with the branch logo (or fallback icon) + branch name.
 * Used in the Cash Analysis tables when the user is viewing "All Branches"
 * so each row makes its campus origin obvious at a glance.
 */
function BranchChip({ name, logo }) {
  if (!name) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-heading font-semibold bg-[#FFE8D6] text-[#9A3412] max-w-[180px] align-middle"
      title={name}
      data-testid="ca-branch-chip"
    >
      <span className="w-4 h-4 rounded-full overflow-hidden flex items-center justify-center shrink-0 bg-white/70">
        {logo
          ? <img src={logo} alt="" className="w-full h-full object-cover" />
          : <Building2 className="w-2.5 h-2.5 text-[#9A3412]" />}
      </span>
      <span className="truncate">{name}</span>
    </span>
  );
}

export default function CashAnalysisPage() {
  const { user } = useAuth();
  const { allBranches, selectedSchool } = useBranch();
  const { frozenBranch, lockedBranchId } = useBranchFreeze();
  const currency = user?.currency_symbol || "₱";
  const peso = (n) =>
    `${currency}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const pesoNeg = (n) =>
    `-${currency}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [branchFilter, setBranchFilter] = useState(() => lockedBranchId || "all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPrev, setShowPrev] = useState(false);

  const [openCashOnHand, setOpenCashOnHand] = useState(false);
  const [openFundTransfer, setOpenFundTransfer] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const branchesForSchool = useMemo(
    () => allBranches.filter((b) => b.school_id === selectedSchool?.id && b.status === "approved"),
    [allBranches, selectedSchool?.id]
  );

  // Frozen branch users always operate within their assigned branch.
  const effBranchFilter = frozenBranch && lockedBranchId ? lockedBranchId : branchFilter;

  const years = useMemo(() => {
    const ys = [];
    for (let y = today.getFullYear() + 1; y >= today.getFullYear() - 5; y--) ys.push(y);
    return ys;
  }, [today]);

  const load = useCallback(async () => {
    if (!selectedSchool) { setData(null); return; }
    setLoading(true);
    try {
      const params = { school_id: selectedSchool.id, year, month };
      if (effBranchFilter !== "all") params.branch_id = effBranchFilter;
      const { data: res } = await api.get("/accounting/cash-analysis", { params });
      setData(res);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [selectedSchool, year, month, effBranchFilter]);

  useEffect(() => { load(); }, [load]);

  const isClosed = data?.period?.status === "closed";
  const showBranchChip = effBranchFilter === "all";

  const descCell = (r) => (
    <div className="inline-flex items-center gap-2 flex-wrap min-w-0 justify-center">
      <span className="truncate">{r.description || "—"}</span>
      {showBranchChip && <BranchChip name={r.branch_name} logo={r.branch_logo_base64} />}
    </div>
  );

  const periodKey = () => ({
    school_id: selectedSchool.id,
    year, month,
    ...(effBranchFilter !== "all" ? { branch_id: effBranchFilter } : {}),
  });

  const doClose = async () => {
    setBusy(true);
    try {
      await api.post("/accounting/cash-analysis/close", periodKey());
      toast.success(`${MONTHS[month - 1]} ${year} is now closed`);
      setConfirmClose(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doReopen = async () => {
    setBusy(true);
    try {
      await api.post("/accounting/cash-analysis/reopen", periodKey());
      toast.success("Month reopened");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    const url = deleteTarget.kind === "cash_on_hand"
      ? `/accounting/cash-on-hand/${deleteTarget.id}`
      : `/accounting/fund-transfer/${deleteTarget.id}`;
    try {
      await api.delete(url);
      toast.success("Removed");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!selectedSchool) {
    return (
      <>
        <Header />
        <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="ca-no-school">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
            <AlertCircle className="w-7 h-7 text-[#E65100]" />
          </div>
          <p className="font-heading text-lg font-semibold text-foreground">Pick a school first</p>
          <p className="text-brand-inkMuted mt-1 text-sm">Cash Analysis is scoped per school.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <Header />
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={load}
            className="rounded-2xl border-brand-purple/30 text-foreground h-11 px-5 hover:bg-brand-purpleLight"
            data-testid="ca-open-record-btn"
          >
            <FolderOpen className="w-4 h-4 mr-2" /> Open Record
          </Button>
          <Button
            onClick={() => { load(); toast.success("Refreshed"); }}
            className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11 px-5"
            data-testid="ca-save-btn"
          >
            <Save className="w-4 h-4 mr-2" /> Save Cash Analysis
          </Button>
        </div>
      </div>

      {/* Period selector */}
      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-5 mb-5" data-testid="ca-filters">
        <div className="grid sm:grid-cols-2 gap-4">
          <FieldBlock label="School">
            <Input value={selectedSchool.name} disabled className="rounded-xl h-11 bg-brand-purpleLight/30" />
          </FieldBlock>
          <FieldBlock label="Branch">
            <select
              value={effBranchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              disabled={frozenBranch}
              title={frozenBranch ? "Locked to your assigned branch" : undefined}
              className="w-full h-11 rounded-xl border border-brand-purple/15 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple disabled:bg-brand-purpleLight/30 disabled:cursor-not-allowed disabled:opacity-80"
              data-testid="ca-filter-branch"
            >
              {!frozenBranch && <option value="all">All Branches</option>}
              {branchesForSchool.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
            </select>
          </FieldBlock>
          <FieldBlock label="Month">
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="w-full h-11 rounded-xl border border-brand-purple/15 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
              data-testid="ca-filter-month"
            >
              {MONTHS.map((m, i) => (<option key={m} value={i + 1}>{m}</option>))}
            </select>
          </FieldBlock>
          <FieldBlock label="Year">
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="w-full h-11 rounded-xl border border-brand-purple/15 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
              data-testid="ca-filter-year"
            >
              {years.map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
          </FieldBlock>
        </div>
        <p className="text-xs text-brand-inkMuted mt-3">
          Period: <span className="font-semibold text-foreground">{MONTHS[month - 1]} {year}</span>
        </p>
      </section>

      {loading || !data ? (
        <div className="bg-white rounded-2xl border border-brand-purple/10 p-10 text-center text-brand-inkMuted" data-testid="ca-loading">
          Loading cash analysis...
        </div>
      ) : (
        <>
          {/* Month status */}
          <section
            className={`rounded-2xl border shadow-sm p-5 mb-5 flex items-center justify-between gap-3 flex-wrap ${
              isClosed ? "bg-[#FFF4E5] border-[#FFB74D]/40" : "bg-white border-brand-purple/10"
            }`}
            data-testid="ca-month-status"
          >
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${
                isClosed ? "bg-[#E65100]/15 text-[#E65100]" : "bg-brand-purpleLight text-brand-purple"
              }`}>
                {isClosed ? <Lock className="w-5 h-5" /> : <Unlock className="w-5 h-5" />}
              </div>
              <div>
                <p className="font-heading text-lg font-semibold text-foreground">
                  Month is {isClosed ? "Closed" : "Open"}
                </p>
                <p className="text-sm text-brand-inkMuted">
                  {isClosed
                    ? `Closed on ${fmtDate((data.period.closed_at || "").slice(0, 10))} · Locked at ${peso(data.period.closed_final_balance)}`
                    : `Close ${MONTHS[month - 1]} ${year} to lock figures for this period.`}
                </p>
              </div>
            </div>
            <Button
              onClick={isClosed ? doReopen : () => setConfirmClose(true)}
              disabled={busy}
              className={`rounded-2xl h-11 px-6 text-white ${
                isClosed
                  ? "bg-[#E65100] hover:bg-[#BF360C]"
                  : "bg-brand-purple hover:bg-brand-purpleHover"
              }`}
              data-testid={isClosed ? "ca-reopen-month-btn" : "ca-close-month-btn"}
            >
              {isClosed ? <><Unlock className="w-4 h-4 mr-2" /> Reopen Month</> : <><Lock className="w-4 h-4 mr-2" /> Close Month</>}
            </Button>
          </section>

          {/* Cash on Hand from Previous Month */}
          <section className="rounded-2xl border border-[#A5D6A7] bg-gradient-to-br from-[#E8F5E9] to-white shadow-sm p-5 mb-5" data-testid="ca-prev-month">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-11 h-11 rounded-2xl bg-[#C8E6C9] flex items-center justify-center shrink-0">
                  <CalendarClock className="w-5 h-5 text-[#2E7D32]" />
                </div>
                <div>
                  <p className="font-heading text-lg font-semibold text-foreground">Cash on Hand from Previous Month</p>
                  <p className="text-sm text-brand-inkMuted">Final cash on hand from the previous month.</p>
                  <p className="text-xs text-brand-inkMuted mt-1">
                    Previous Month: <span className="font-semibold text-foreground">{MONTHS[data.previous_month.month - 1]} {data.previous_month.year}</span>
                    {!data.previous_month.was_closed && (
                      <span className="ml-2 inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#FFE0B2] text-[#E65100]">
                        Live (not closed)
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-heading text-3xl font-bold text-foreground" data-testid="ca-prev-balance">
                  {peso(data.previous_month.closing_balance)}
                </p>
                <button
                  type="button"
                  onClick={() => setShowPrev((v) => !v)}
                  className="mt-1 inline-flex items-center gap-1 text-xs text-brand-purpleHover hover:text-brand-purple font-semibold"
                  data-testid="ca-toggle-prev-breakdown"
                >
                  {showPrev ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  View Breakdown
                </button>
              </div>
            </div>
            {showPrev && (
              <div className="mt-3 rounded-xl bg-white/70 border border-[#A5D6A7]/40 p-3 text-xs text-foreground">
                Tip: To lock this balance, close the previous month from the Cash Analysis page for that period.
                When uncertain, the rollover uses a live recompute of last month's totals.
              </div>
            )}
          </section>

          {/* Cash on Hand */}
          <SimpleSection
            title="Cash on Hand"
            cols={["Date", "Amount", "Description", "Added By", ""]}
            rows={data.sections.cash_on_hand}
            renderRow={(r) => [
              fmtDate(r.date),
              <span className="font-semibold">{peso(r.amount)}</span>,
              descCell(r),
              r.added_by_name || "—",
              !isClosed && (
                <button onClick={() => setDeleteTarget({ kind: "cash_on_hand", ...r })} className="text-[#C62828] hover:underline text-xs" data-testid={`ca-delete-cash-on-hand-${r.id}`}>
                  <Trash2 className="w-3.5 h-3.5 inline" />
                </button>
              ),
            ]}
            emptyText="No cash on hand records for this period."
            totalLabel="Total Initial Cash on Hand"
            totalValue={peso(data.totals.initial_cash_on_hand)}
            testid="ca-cash-on-hand"
            action={
              !isClosed && (
                <Button onClick={() => setOpenCashOnHand(true)} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-10 px-5" data-testid="ca-add-cash-on-hand-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Add Cash on Hand
                </Button>
              )
            }
          />

          {/* Cash From Income */}
          <SimpleSection
            title="Cash From Income"
            cols={["Date", "Amount", "Description", "Received By"]}
            rows={data.sections.cash_from_income}
            renderRow={(r) => [
              fmtDate(r.date),
              <span className="font-semibold">{peso(r.amount)}</span>,
              descCell(r),
              r.received_by || "—",
            ]}
            emptyText="No cash payments from Income in this period."
            totalLabel="Total Cash From Income"
            totalValue={peso(data.totals.cash_from_income)}
            testid="ca-cash-from-income"
          />

          {/* Cash From Other Income */}
          <SimpleSection
            title="Cash From Other Income"
            cols={["Date", "Amount", "Description", "Received By"]}
            rows={data.sections.cash_from_other_income}
            renderRow={(r) => [
              fmtDate(r.date),
              <span className="font-semibold">{peso(r.amount)}</span>,
              descCell(r),
              r.received_by || "—",
            ]}
            emptyText="No cash entries from Accounting > Income in this period."
            totalLabel="Total Cash From Other Income"
            totalValue={peso(data.totals.cash_from_other_income)}
            testid="ca-cash-from-other-income"
          />

          {/* Cash Out From Expenses */}
          <SimpleSection
            title="Cash Out From Expenses"
            cols={["Date", "Amount", "Description", "Disbursed By"]}
            rows={data.sections.cash_out_from_expenses}
            renderRow={(r) => [
              fmtDate(r.date),
              <span className="font-semibold">{peso(r.amount)}</span>,
              descCell(r),
              r.disbursed_by || "—",
            ]}
            emptyText="No cash expenses in this period."
            totalLabel="Total Cash Out From Expenses"
            totalValue={peso(data.totals.cash_out_from_expenses)}
            testid="ca-cash-out-from-expenses"
          />

          {/* Fund Transfer */}
          <SimpleSection
            title="Fund Transfer"
            titleIcon={<ArrowRightLeft className="w-5 h-5 text-brand-purple" />}
            cols={["Date", "Amount", "Description", "Mode", "Reference No.", "Transferred By", ""]}
            rows={data.sections.fund_transfers}
            renderRow={(r) => [
              fmtDate(r.date),
              <span className="font-semibold">{peso(r.amount)}</span>,
              descCell(r),
              <span className="capitalize">{formatMode(r.mode)}</span>,
              <span className="font-mono text-xs">{r.reference_no || "—"}</span>,
              r.transferred_by_name || "—",
              !isClosed && (
                <button onClick={() => setDeleteTarget({ kind: "fund_transfer", ...r })} className="text-[#C62828] hover:underline text-xs" data-testid={`ca-delete-fund-transfer-${r.id}`}>
                  <Trash2 className="w-3.5 h-3.5 inline" />
                </button>
              ),
            ]}
            emptyText="No fund transfers in this period."
            totalLabel="Total Fund Transfer"
            totalValue={peso(data.totals.fund_transfer)}
            testid="ca-fund-transfer"
            action={
              !isClosed && (
                <Button onClick={() => setOpenFundTransfer(true)} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-10 px-5" data-testid="ca-add-fund-transfer-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Transfer Fund
                </Button>
              )
            }
          />

          {/* Cash Summary */}
          <section className="bg-white rounded-2xl border-2 border-brand-purple/30 shadow-sm p-5 mt-5" data-testid="ca-summary">
            <h2 className="font-heading text-xl font-semibold mb-4">Cash Summary</h2>
            <SummaryRow label="Cash on Hand from Previous Month" value={peso(data.previous_month.closing_balance)} />
            <SummaryRow label="Total Initial Cash on Hand" value={peso(data.totals.initial_cash_on_hand)} />
            <SummaryRow label="Total Cash From Income" value={peso(data.totals.cash_from_income)} />
            <SummaryRow label="Total Cash From Other Income" value={peso(data.totals.cash_from_other_income)} />
            <SummaryRow label="Total Cash Out From Expenses" value={pesoNeg(data.totals.cash_out_from_expenses)} negative />
            <SummaryRow label="Total Fund Transfer" value={pesoNeg(data.totals.fund_transfer)} negative />
            <div className="border-t border-brand-purple/20 mt-3 pt-3 flex items-center justify-between">
              <p className="font-heading text-lg font-semibold">Final Cash on Hand</p>
              <p className="font-heading text-2xl font-bold text-brand-purple" data-testid="ca-final-balance">
                {peso(data.final_cash_on_hand)}
              </p>
            </div>
          </section>
        </>
      )}

      {/* Add Cash on Hand */}
      <EntryDialog
        open={openCashOnHand}
        onOpenChange={setOpenCashOnHand}
        title="Add Cash on Hand"
        description="Record a cash injection (petty cash, opening balance, owner deposit)."
        endpoint="/accounting/cash-on-hand"
        baseBody={{ school_id: selectedSchool.id }}
        defaultDate={defaultDateInPeriod(year, month)}
        branchOptions={branchesForSchool}
        branchPreset={effBranchFilter !== "all" ? effBranchFilter : ""}
        showReference={false}
        onSuccess={load}
      />

      {/* Add Fund Transfer */}
      <EntryDialog
        open={openFundTransfer}
        onOpenChange={setOpenFundTransfer}
        title="Transfer Fund"
        description="Record cash moved out (bank deposit, transfer between branches)."
        endpoint="/accounting/fund-transfer"
        baseBody={{ school_id: selectedSchool.id }}
        defaultDate={defaultDateInPeriod(year, month)}
        branchOptions={branchesForSchool}
        branchPreset={effBranchFilter !== "all" ? effBranchFilter : ""}
        showReference={true}
        showPaymentMethod={true}
        onSuccess={load}
      />

      {/* Close confirmation */}
      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent className="rounded-3xl border-brand-purple/20" data-testid="ca-confirm-close-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Close {MONTHS[month - 1]} {year}?</AlertDialogTitle>
            <AlertDialogDescription>
              The Final Cash on Hand of <span className="font-semibold text-foreground">{peso(data?.final_cash_on_hand)}</span> will be locked
              and used as the opening balance for next month. You can reopen the period if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-brand-purple/30">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doClose} disabled={busy} className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="ca-confirm-close-btn">
              {busy ? "Closing..." : "Close month"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-3xl border-brand-purple/20" data-testid="ca-confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Delete this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.description}" — {peso(deleteTarget?.amount)} will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-brand-purple/30">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="rounded-full bg-[#C62828] hover:bg-[#B71C1C] text-white" data-testid="ca-confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Header() {
  return (
    <div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Cash Analysis</h1>
      <p className="text-brand-inkMuted mt-0.5 text-sm">
        Track cash on hand, cash income, expenses, fund transfers, and ending cash balance.
      </p>
    </div>
  );
}

function FieldBlock({ label, children }) {
  return (
    <div>
      <Label className="font-heading text-sm">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function SimpleSection({ title, titleIcon, cols, rows, renderRow, emptyText, totalLabel, totalValue, action, testid }) {
  return (
    <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden mb-4" data-testid={testid}>
      <header className="px-4 py-3 border-b border-brand-purple/10 flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
          {titleIcon} {title}
        </h2>
        {action}
      </header>
      {(!rows || rows.length === 0) ? (
        <div className="p-6 text-center text-brand-inkMuted text-sm" data-testid={`${testid}-empty`}>{emptyText}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F1F5FF] text-brand-inkMuted text-[11px]">
              <tr>
                {cols.map((c, i) => (
                  <th key={i} className={`px-3 py-3 font-heading font-semibold tracking-wider ${(i === 1 || i === 2) ? "text-center" : "text-left"}`}>{c.toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.id || idx} className="border-t border-brand-purple/5 hover:bg-[#FAFBFE]">
                  {renderRow(r).map((cell, i) => (
                    <td key={i} className={`px-3 py-3 ${(i === 1 || i === 2) ? "text-center" : "text-left"}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <footer className="px-4 py-3 bg-[#F8FAFF] border-t border-brand-purple/10 flex items-center justify-between">
        <span className="text-sm font-heading font-semibold text-foreground">{totalLabel}</span>
        <span className="text-sm font-heading font-bold text-brand-purple" data-testid={`${testid}-total`}>{totalValue}</span>
      </footer>
    </section>
  );
}

function SummaryRow({ label, value, negative }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-foreground">{label}</span>
      <span className={negative ? "text-[#C62828] font-semibold" : "font-semibold"}>{value}</span>
    </div>
  );
}

function defaultDateInPeriod(year, month) {
  const today = new Date();
  if (today.getFullYear() === year && today.getMonth() + 1 === month) {
    return today.toISOString().slice(0, 10);
  }
  return `${year}-${String(month).padStart(2, "0")}-01`;
}

function EntryDialog({
  open, onOpenChange, title, description, endpoint, baseBody,
  defaultDate, branchOptions, branchPreset, showReference, showPaymentMethod, onSuccess,
}) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    branch_id: "", date: defaultDate, amount: "", description: "", reference_no: "", mode: "cash",
  });

  useEffect(() => {
    if (open) {
      setForm({
        branch_id: branchPreset || (branchOptions[0]?.id || ""),
        date: defaultDate,
        amount: "",
        description: "",
        reference_no: "",
        mode: "cash",
      });
    }
  }, [open, branchPreset, defaultDate, branchOptions]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.branch_id) { toast.error("Pick a branch"); return; }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be > 0"); return; }
    setBusy(true);
    try {
      const body = {
        ...baseBody,
        branch_id: form.branch_id,
        date: form.date,
        amount: amt,
        description: form.description.trim(),
      };
      if (showReference) body.reference_no = form.reference_no.trim();
      if (showPaymentMethod) body.mode = form.mode;
      await api.post(endpoint, body);
      toast.success("Saved");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl border-brand-purple/20 max-w-lg" data-testid={`ca-entry-dialog-${endpoint}`}>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <FieldBlock label="Branch">
            <select
              required
              value={form.branch_id}
              onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
              className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm"
              data-testid="ca-entry-branch"
            >
              <option value="">Select branch...</option>
              {branchOptions.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
            </select>
          </FieldBlock>
          <div className="grid sm:grid-cols-2 gap-3">
            <FieldBlock label="Date">
              <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" data-testid="ca-entry-date" />
            </FieldBlock>
            <FieldBlock label="Amount">
              <Input type="number" step="0.01" min="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" data-testid="ca-entry-amount" />
            </FieldBlock>
          </div>
          <FieldBlock label="Description">
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" data-testid="ca-entry-description" />
          </FieldBlock>
          {showPaymentMethod && (
            <FieldBlock label="Mode of Payment">
              <select
                value={form.mode}
                onChange={(e) => setForm({ ...form, mode: e.target.value })}
                className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm"
                data-testid="ca-entry-mode"
              >
                {PAYMENT_MODES.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
              </select>
            </FieldBlock>
          )}
          {showReference && (
            <FieldBlock label="Reference No.">
              <Input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" placeholder="e.g. DEP-0001" data-testid="ca-entry-reference" />
            </FieldBlock>
          )}
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full border-brand-purple/30">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="ca-entry-submit">
              {busy ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
